import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg', 'bmp'];
const TEXT_EXTENSIONS = ['txt', 'json', 'csv', 'md', 'js', 'jsx', 'ts', 'tsx', 'html', 'css', 'log'];

const getExtension = (filename = '') => {
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

const AttachmentPreviewModal = ({
  isOpen,
  toggle,
  attachment,
  url,
  isLoading,
  error,
}) => {
  const [textContent, setTextContent] = useState('');
  const [textLoading, setTextLoading] = useState(false);
  const [textError, setTextError] = useState('');

  const fileName = attachment ? attachment.name || attachment.key || 'Attachment' : 'Attachment';
  const extension = getExtension(fileName);
  const isImage = IMAGE_EXTENSIONS.includes(extension);
  const isPdf = extension === 'pdf';
  const isText = TEXT_EXTENSIONS.includes(extension);

  useEffect(() => {
    let isMounted = true;
    if (isOpen && url && isText) {
      setTextLoading(true);
      setTextError('');
      axios
        .get(url, { responseType: 'text' })
        .then((res) => {
          if (isMounted) {
            const content = typeof res.data === 'string' ? res.data : JSON.stringify(res.data, null, 2);
            setTextContent(content);
            setTextLoading(false);
          }
        })
        .catch(() => {
          if (isMounted) {
            setTextError('Unable to preview text content directly.');
            setTextLoading(false);
          }
        });
    } else {
      setTextContent('');
      setTextError('');
      setTextLoading(false);
    }
    return () => {
      isMounted = false;
    };
  }, [isOpen, url, isText]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle} className="border-bottom">
        <div className="d-flex align-items-center">
          <i
            className={`wb ${isImage ? 'webconnect-image mr-2 text-primary' : 'webconnect-file-text mr-2 text-info'}`}
            style={{ fontSize: '18px' }}
          />
          <span className="font-weight-bold" style={{ fontSize: '15px' }}>
            {fileName}
          </span>
        </div>
      </ModalHeader>

      <ModalBody className="p-4" style={{ minHeight: '260px' }}>
        {isLoading ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-5">
            <div
              className="spinner-border text-primary mb-3"
              role="status"
              style={{ width: '2.5rem', height: '2.5rem' }}
            >
              <span className="sr-only">Loading...</span>
            </div>
            <p className="text-muted mb-0 font-weight-medium">Loading attachment preview...</p>
          </div>
        ) : error ? (
          <div className="alert alert-danger mb-0" role="alert">
            <i className="wb webconnect-exclamation-triangle mr-2" />
            {error}
          </div>
        ) : url ? (
          <div className="d-flex flex-column align-items-center justify-content-center">
            {isImage && (
              <div
                style={{
                  background: '#f8f9fa',
                  borderRadius: '12px',
                  padding: '16px',
                  width: '100%',
                  textAlign: 'center',
                }}
              >
                <img
                  src={url}
                  alt={fileName}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '65vh',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)',
                  }}
                />
              </div>
            )}

            {isPdf && (
              <iframe
                src={url}
                title={fileName}
                style={{
                  width: '100%',
                  height: '65vh',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                }}
              />
            )}

            {isText && (
              <div
                className="w-100 text-left"
                style={{
                  maxHeight: '65vh',
                  overflow: 'auto',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '16px',
                }}
              >
                {textLoading ? (
                  <p className="text-muted mb-0">Loading file contents...</p>
                ) : textError ? (
                  <div>
                    <p className="text-muted mb-2">{textError}</p>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-outline-primary"
                    >
                      Open in New Tab
                    </a>
                  </div>
                ) : (
                  <pre
                    style={{
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: '13px',
                      fontFamily: 'monospace',
                    }}
                  >
                    {textContent}
                  </pre>
                )}
              </div>
            )}

            {!isImage && !isPdf && !isText && (
              <div className="py-4 text-center">
                <i
                  className="wb webconnect-file-earmark-text mb-3 text-muted"
                  style={{ fontSize: '48px' }}
                />
                <p className="text-muted mb-3 font-weight-medium">
                  Direct preview is not available for this file type.
                </p>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-theme-submit"
                >
                  Open / Download File
                </a>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-5 text-muted">No preview URL available</div>
        )}
      </ModalBody>

      <ModalFooter className="d-flex justify-content-between align-items-center">
        <div className="text-muted small text-truncate" style={{ maxWidth: '300px' }}>
          {fileName}
        </div>
        <div>
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-secondary btn-sm mr-2"
            >
              Open in New Tab
            </a>
          )}
          <button type="button" className="btn btn-secondary btn-sm" onClick={toggle}>
            Close
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

AttachmentPreviewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  attachment: PropTypes.object,
  url: PropTypes.string,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
};

export default AttachmentPreviewModal;
